import type { ReactElement, ReactNode } from 'react'
import { ArrowRight } from 'lucide-react'
import { IconButton, IconButtonLink } from '@/components/play'
import { cn } from '@/lib/utils'

interface ToolbarClusterProps {
  /** Optional accessibility label for the cluster region. */
  ariaLabel?: string
  children: ReactNode
  className?: string
}

/**
 * Horizontal cluster of toolbar controls. Used to group inputs (Cameras)
 * separately from outputs (Broadcasts) in the spatial "sources → destinations"
 * grammar of the event toolbar.
 */
export function ToolbarCluster({ ariaLabel, children, className }: ToolbarClusterProps) {
  return (
    <div role="group" aria-label={ariaLabel} className={cn('flex items-center gap-2', className)}>
      {children}
    </div>
  )
}

interface ToolbarActionProps {
  /** A Lucide icon. */
  icon: ReactElement
  /** The action's name: read by assistive technology and shown as its tooltip. */
  label: string
  /** Internal react-router path — renders a link. */
  to?: string
  /** External URL — renders a link. */
  href?: string
  target?: string
  onClick?: () => void
  className?: string
  disabled?: boolean
}

/** One toolbar action: a Play ghost icon button, or an icon link for `to` and `href`, with its name as the tooltip. */
export function ToolbarAction({ icon, label, to, href, target, onClick, className, disabled }: ToolbarActionProps) {
  if (to) {
    return <IconButtonLink to={to} label={label} variant="ghost" className={className}>{icon}</IconButtonLink>
  }
  if (href) {
    return (
      <IconButtonLink href={href} target={target} rel={target === '_blank' ? 'noreferrer' : undefined} label={label} variant="ghost" className={className}>
        {icon}
      </IconButtonLink>
    )
  }
  return <IconButton label={label} variant="ghost" onClick={onClick} disabled={disabled} className={className}>{icon}</IconButton>
}

/**
 * Vertical hairline divider — separates the Destinations group from the
 * event-level utility controls (Status, Settings).
 */
export function ToolbarDivider({ className }: { className?: string }) {
  return <div aria-hidden="true" className={cn('hidden h-6 w-px bg-neutral-4 sm:block', className)} />
}

/**
 * Thin flow line + chevron between Cameras and Broadcasts clusters.
 * Reinforces the “sources → destinations” mental model spatially.
 */
export function ToolbarFlow({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn('hidden min-w-[48px] flex-1 items-center gap-2 px-3 text-neutral-6 lg:flex', className)}
    >
      <div className="h-px flex-1 bg-neutral-4" />
      <ArrowRight className="h-3.5 w-3.5" />
      <div className="h-px flex-1 bg-neutral-4" />
    </div>
  )
}
